import React from 'react';
import { Play, Lock, Crown, Users, Clock, Trophy } from 'lucide-react';
import { 
  getSubscriptionTierName, 
  getSubscriptionTierBadgeColor,
  hasGameAccess,
  SUBSCRIPTION_TIERS 
} from '../../constants/games';

const GameCard = ({ 
  game, 
  userSubscription = SUBSCRIPTION_TIERS.FREE,
  onCreateGame, 
  isLoading = false,
  showSubscriptionTier = true,
  className = ""
}) => {
  const hasAccess = hasGameAccess(userSubscription, game.subscriptionTier);
  const isPremium = game.subscriptionTier !== SUBSCRIPTION_TIERS.FREE;

  const notifyUpgradeRequired = (requiredTier) => {
    // This would typically show a modal or toast
    console.log(`Upgrade to ${getSubscriptionTierName(requiredTier)} required`);
  };

  const handleCreateGame = () => {
    if (!hasAccess) {
      notifyUpgradeRequired(game.subscriptionTier);
      return;
    }
    onCreateGame(game.id);
  };

  return (
    <div className={`card hover:scale-105 transition-all duration-300 ${className} ${
      !hasAccess ? 'opacity-75' : ''
    }`}>
      <div className="text-center">
        {/* Game Icon */}
        <div className="text-6xl mb-4 relative">
          {game.emoji}
          {isPremium && (
            <div className="absolute -top-2 -right-2">
              <Crown className="h-6 w-6 text-yellow-400" />
            </div>
          )}
        </div>

        {/* Game Info */}
        <h3 className="text-2xl font-bold text-discord-lightest mb-2">
          {game.name}
        </h3>
        
        <p className="text-discord-light mb-4">
          {game.description}
        </p>

        {/* Subscription Tier Badge */}
        {showSubscriptionTier && (
          <div className="mb-4">
            <span className={`badge ${getSubscriptionTierBadgeColor(game.subscriptionTier)}`}>
              {getSubscriptionTierName(game.subscriptionTier)}
            </span>
          </div>
        )}

        {/* Difficulty Tags */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {game.difficulties?.map((diff) => (
            <span key={diff} className="badge badge-primary">
              {diff.charAt(0).toUpperCase() + diff.slice(1)}
            </span>
          ))}
        </div>

        {/* Game Stats */}
        <div className="flex items-center justify-center space-x-4 text-sm text-discord-light mb-4">
          <div className="flex items-center">
            <Users className="h-4 w-4 mr-1" />
            {game.maxPlayers} player{game.maxPlayers > 1 ? 's' : ''}
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            {Math.floor(game.timeLimit / 60)}m {game.timeLimit % 60}s
          </div>
        </div>

        {/* Features */}
        {game.features && (
          <div className="mb-4">
            <div className="flex flex-wrap justify-center gap-1 text-xs">
              {game.features.singlePlayer && (
                <span className="badge badge-outline">Single Player</span>
              )}
              {game.features.multiplayer && (
                <span className="badge badge-outline">Multiplayer</span>
              )}
              {game.features.aiOpponent && (
                <span className="badge badge-outline">AI Opponent</span>
              )}
              {game.features.statistics && (
                <span className="badge badge-outline">Statistics</span>
              )}
              {game.features.leaderboard && (
                <span className="badge badge-outline">Leaderboard</span>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleCreateGame}
          disabled={isLoading || !hasAccess}
          className={`btn w-full ${
            hasAccess 
              ? 'btn-primary' 
              : 'btn-disabled bg-discord-darker text-discord-light cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <div className="loading loading-spinner loading-sm"></div>
          ) : hasAccess ? (
            <>
              <Play className="h-4 w-4 mr-2" />
              Play Now
            </>
          ) : (
            <>
              <Lock className="h-4 w-4 mr-2" />
              {getSubscriptionTierName(game.subscriptionTier)} Required
            </>
          )}
        </button>

        {/* Upgrade Hint */}
        {!hasAccess && (
          <p className="text-xs text-discord-light mt-2">
            Upgrade to {getSubscriptionTierName(game.subscriptionTier)} to unlock this game
          </p>
        )}
      </div>
    </div>
  );
};

export default GameCard;
